import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalComponentBaseProps } from '@pcmp/react-providers';
import { ModalHeader, Sheets, ModalBody } from '@ingka/modal';
import RadioButtonGroup from '@ingka/radio-button-group';
import { useTrackContent, useTrackInteraction } from '@/lib/hooks/tracking';
import { useModalGoToProfile } from '@/lib/hooks/modal';
import { useAppConfig } from '@/redux/features/app-config/hooks';
import { useAppDispatch } from '@/redux/hooks';
import { setValue } from '@/redux/features/app-config/slice';

const ChangeThemeSider: FC<ModalComponentBaseProps> = ({
  handleDismiss,
  ...rest
}) => {
  // Hooks
  const { darkMode } = useAppConfig();
  const dispatch = useAppDispatch();
  const [trackContent] = useTrackContent();
  const [trackInteraction] = useTrackInteraction();
  const { t } = useTranslation();
  // Event handlers
  const onThemeChange = () => {
    const value = !darkMode;
    dispatch(setValue({ key: 'darkMode', value }));
    trackInteraction({
      type: 'click',
      action: 'change-theme',
      theme: value ? 'dark' : 'light',
    });
  };

  useEffect(() => {
    trackContent({ content: 'change-theme', type: 'sider' });
  }, []);

  const goBack = useModalGoToProfile();

  return (
    <Sheets
      {...rest}
      data-testid="changeThemeModal"
      aria-label={t(`label.changeTheme.${darkMode ? 'darkMode' : 'lightMode'}`)}
      footer={null}
      header={
        <ModalHeader
          ariaCloseTxt={t('aria.close')}
          title={t(`label.changeTheme.${darkMode ? 'darkMode' : 'lightMode'}`)}
          backBtnClick={goBack}
        />
      }
    >
      <ModalBody>
        <RadioButtonGroup
          onChange={onThemeChange}
          list={[
            {
              id: 'light',
              name: 'theme',
              label: t('label.changeTheme.lightMode'),
              value: 'light',
              disabled: false,
              checked: !darkMode,
            },
            {
              id: 'dark',
              name: 'theme',
              label: t('label.changeTheme.darkMode'),
              value: 'dark',
              disabled: false,
              checked: darkMode,
            },
          ]}
        />
      </ModalBody>
    </Sheets>
  );
};

export default ChangeThemeSider;
